/**
 * Rapprochement bancaire — Page dédiée (multi-comptes + saisie manuelle + solde temps réel).
 * Route : /admin/rapprochement/banque
 * RBAC  : lecture `finance.cockpit.view`/`wave.view` — écriture `wave.reconcile.manual`
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import BankAnalyticsTab from '../../components/bank/BankAnalyticsTab';
import BankCFODashboardCards from '../../components/bank/BankCFODashboardCards';
import {
  Landmark, Plus, RefreshCw, Wallet, TrendingUp, TrendingDown, Trash2,
  Edit3, Upload, ArrowUpRight, ArrowDownRight, Building2, Loader2, X, Link2,
  CheckCircle2, MinusCircle, Sparkles, FileDown, ChevronLeft, ChevronRight,
  Ban, Download, BarChart3, Tag, Lightbulb,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { RequirePermission } from '../../components/permissions/RequirePermission';
import { PERMS } from '../../utils/permissions';
import StatementUploadDialog from '../../components/bank/StatementUploadDialog';
import ReconciliationDialog from '../../components/bank/ReconciliationDialog';
import BankCFOCards from '../../components/bank/BankCFOCards';
import ExceptionsView from '../../components/bank/ExceptionsView';
import ClosePeriodDialog from '../../components/bank/ClosePeriodDialog';
import PeriodPicker from '../../components/bank/PeriodPicker';
import BankCategorizeDialog from '../../components/bank/BankCategorizeDialog';
import ExpenseRulesPanel from '../../components/bank/ExpenseRulesPanel';
import { BulkActionBar, confirmBulk } from '../../components/common/BulkActionBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => {
  if (v == null || isNaN(v)) return '—';
  return Math.round(v).toLocaleString('fr-FR');
};
const fxof = (v) => `${fmt(v)} XOF`;

/* Types d'opérations bancaires — libellés FR + icônes de direction */
const TX_TYPES = [
  { value: 'virement_entrant', label: 'Virement entrant', direction: 'credit', color: 'emerald' },
  { value: 'virement_sortant', label: 'Virement sortant', direction: 'debit', color: 'rose' },
  { value: 'cheque_recu', label: 'Chèque reçu', direction: 'credit', color: 'emerald' },
  { value: 'cheque_emis', label: 'Chèque émis', direction: 'debit', color: 'rose' },
  { value: 'depot_especes', label: 'Dépôt espèces', direction: 'credit', color: 'emerald' },
  { value: 'retrait', label: 'Retrait', direction: 'debit', color: 'rose' },
  { value: 'prelevement', label: 'Prélèvement', direction: 'debit', color: 'rose' },
  { value: 'frais_bancaires', label: 'Frais bancaires', direction: 'debit', color: 'amber' },
  { value: 'agios', label: 'Agios', direction: 'debit', color: 'amber' },
  { value: 'commission', label: 'Commission', direction: 'debit', color: 'amber' },
  { value: 'virement_interne', label: 'Virement interne (Wave↔Banque)', direction: 'debit', color: 'blue' },
  { value: 'autre', label: 'Autre', direction: 'debit', color: 'zinc' },
];

const typeLabel = (t) => TX_TYPES.find((x) => x.value === t)?.label || t;
const typeMeta = (t) => TX_TYPES.find((x) => x.value === t) || { color: 'zinc' };

/* ────────────────── ACCOUNT DIALOG (créer/éditer) ────────────────── */
const AccountDialog = ({ open, onClose, onSaved, account }) => {
  const [form, setForm] = useState({
    bank_name: '',
    account_number: '',
    account_label: '',
    initial_balance: 0,
    initial_balance_date: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!account;

  useEffect(() => {
    if (account) {
      setForm({
        bank_name: account.bank_name || '',
        account_number: account.account_number || '',
        account_label: account.account_label || '',
        initial_balance: account.initial_balance || 0,
        initial_balance_date: (account.initial_balance_date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        notes: account.notes || '',
      });
    } else {
      setForm({
        bank_name: '', account_number: '', account_label: '',
        initial_balance: 0, initial_balance_date: new Date().toISOString().slice(0, 10), notes: '',
      });
    }
  }, [account, open]);

  const submit = async () => {
    if (!form.bank_name.trim() || !form.account_number.trim() || !form.account_label.trim()) {
      toast({ title: 'Champs obligatoires manquants', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/bank/accounts/${account.id}`, {
          bank_name: form.bank_name,
          account_label: form.account_label,
          notes: form.notes,
          // Correction possible du solde initial (rare — cas d'erreur de saisie)
          initial_balance: Number(form.initial_balance) || 0,
          initial_balance_date: new Date(form.initial_balance_date).toISOString(),
        });
        toast({ title: 'Compte mis à jour' });
      } else {
        await axios.post(`${API}/bank/accounts`, {
          bank_name: form.bank_name,
          account_number: form.account_number,
          account_label: form.account_label,
          initial_balance: Number(form.initial_balance) || 0,
          initial_balance_date: new Date(form.initial_balance_date).toISOString(),
          notes: form.notes,
        });
        toast({ title: 'Compte créé' });
      }
      onSaved();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="bank-account-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le compte' : 'Ajouter un compte bancaire'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Banque *</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                placeholder="Ex : Versus Bank" data-testid="bank-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">N° Compte *</Label>
              <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                disabled={isEdit} placeholder="14359750002" data-testid="bank-account-number-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Libellé *</Label>
            <Input value={form.account_label} onChange={(e) => setForm({ ...form, account_label: e.target.value })}
              placeholder="Compte principal Versus" data-testid="bank-account-label-input" />
          </div>
          {!isEdit ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Solde initial (XOF)</Label>
                <Input type="number" value={form.initial_balance}
                  onChange={(e) => setForm({ ...form, initial_balance: e.target.value })}
                  className="tabular-nums" data-testid="bank-initial-balance-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Au</Label>
                <Input type="date" value={form.initial_balance_date}
                  onChange={(e) => setForm({ ...form, initial_balance_date: e.target.value })}
                  data-testid="bank-initial-date-input" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg">
              <div className="col-span-2 text-xs text-amber-700 font-medium flex items-center gap-1.5 mb-1">
                ⚠️ Correction du solde initial — à n&apos;utiliser qu&apos;en cas d&apos;erreur de saisie (le solde calculé sera recalculé).
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Solde initial (XOF)</Label>
                <Input type="number" value={form.initial_balance}
                  onChange={(e) => setForm({ ...form, initial_balance: e.target.value })}
                  className="tabular-nums" data-testid="bank-initial-balance-edit-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Au</Label>
                <Input type="date" value={form.initial_balance_date}
                  onChange={(e) => setForm({ ...form, initial_balance_date: e.target.value })}
                  data-testid="bank-initial-date-edit-input" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="(facultatif)" data-testid="bank-account-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button onClick={submit} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bank-account-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              {isEdit ? 'Enregistrer' : 'Créer le compte'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ────────────────── TX DIALOG (nouvelle opération / édition) ────────────────── */
const TxDialog = ({ open, onClose, onSaved, accountId, accounts, editingTx = null }) => {
  const isEdit = !!editingTx;
  const today = new Date().toISOString().slice(0, 10);
  const toDateInput = (v) => {
    if (!v) return today;
    try { return new Date(v).toISOString().slice(0, 10); } catch { return today; }
  };
  const [form, setForm] = useState({
    account_id: accountId || '',
    operation_date: today,
    value_date: today,
    type: 'virement_sortant',
    direction: 'debit',
    label: '',
    amount: '',
    counterparty: '',
    reference: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit && editingTx) {
      // Pré-remplissage en mode édition
      setForm({
        account_id: editingTx.account_id || accountId || accounts[0]?.id || '',
        operation_date: toDateInput(editingTx.operation_date),
        value_date: toDateInput(editingTx.value_date),
        type: editingTx.type || 'virement_sortant',
        direction: editingTx.direction || 'debit',
        label: editingTx.label || '',
        amount: String(editingTx.amount ?? ''),
        counterparty: editingTx.counterparty || '',
        reference: editingTx.reference || '',
        notes: editingTx.notes || '',
      });
    } else {
      setForm((f) => ({ ...f, account_id: accountId || accounts[0]?.id || '', operation_date: today, value_date: today }));
    }
  }, [open, editingTx, accountId, accounts]);

  const onTypeChange = (type) => {
    const meta = TX_TYPES.find((x) => x.value === type);
    setForm({ ...form, type, direction: meta?.direction || 'debit' });
  };

  const submit = async () => {
    if (!form.account_id || !form.label.trim() || !form.amount || Number(form.amount) <= 0) {
      toast({ title: 'Compte, libellé et montant > 0 obligatoires', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        account_id: form.account_id,
        operation_date: new Date(form.operation_date).toISOString(),
        value_date: form.value_date ? new Date(form.value_date).toISOString() : null,
        label: form.label,
        reference: form.reference || null,
        type: form.type,
        direction: form.direction,
        amount: Number(form.amount),
        counterparty: form.counterparty || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        await axios.patch(`${API}/bank/transactions/${editingTx.id}`, body);
        toast({ title: 'Opération mise à jour' });
      } else {
        await axios.post(`${API}/bank/transactions`, body);
        toast({ title: 'Opération enregistrée' });
      }
      onSaved();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bank-tx-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier l\'opération bancaire' : 'Nouvelle opération bancaire'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Compte *</Label>
            <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              className="w-full h-9 rounded-md border border-zinc-200 text-sm px-2 mt-1"
              data-testid="tx-account-select">
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.bank_name} — {a.account_label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Type d&apos;opération *</Label>
              <select value={form.type} onChange={(e) => onTypeChange(e.target.value)}
                className="w-full h-9 rounded-md border border-zinc-200 text-sm px-2 mt-1"
                data-testid="tx-type-select">
                {TX_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Sens *</Label>
              <div className="flex gap-1 mt-1">
                <button type="button"
                  onClick={() => setForm({ ...form, direction: 'credit' })}
                  className={`flex-1 h-9 rounded-md text-xs font-semibold border transition ${form.direction === 'credit' ? 'bg-emerald-100 border-emerald-300 text-emerald-800' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}
                  data-testid="tx-direction-credit">
                  <ArrowUpRight className="h-3.5 w-3.5 inline mr-1" /> Crédit
                </button>
                <button type="button"
                  onClick={() => setForm({ ...form, direction: 'debit' })}
                  className={`flex-1 h-9 rounded-md text-xs font-semibold border transition ${form.direction === 'debit' ? 'bg-rose-100 border-rose-300 text-rose-800' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}
                  data-testid="tx-direction-debit">
                  <ArrowDownRight className="h-3.5 w-3.5 inline mr-1" /> Débit
                </button>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Libellé *</Label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Ex : VIREMENT CLIENT TRANSCAO / CHEQUE N°1234 EMIS FOURNISSEUR ABC" data-testid="tx-label-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Montant (XOF) *</Label>
              <Input type="number" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="tabular-nums" placeholder="0" data-testid="tx-amount-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Référence</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="N° chèque, ref virement…" data-testid="tx-reference-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Date opération *</Label>
              <Input type="date" value={form.operation_date}
                onChange={(e) => setForm({ ...form, operation_date: e.target.value })}
                data-testid="tx-operation-date-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Date valeur</Label>
              <Input type="date" value={form.value_date}
                onChange={(e) => setForm({ ...form, value_date: e.target.value })}
                data-testid="tx-value-date-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Contrepartie</Label>
            <Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
              placeholder="Nom du client, fournisseur…" data-testid="tx-counterparty-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button onClick={submit} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="tx-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              {isEdit ? 'Enregistrer les modifications' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ────────────────── ACCOUNT CARD ────────────────── */
const AccountCard = ({ account, isSelected, onClick, onEdit }) => {
  const variation = account.variation_7d || 0;
  return (
    <button
      onClick={onClick}
      className={`text-left p-4 rounded-xl border transition-all duration-200 relative group ${
        isSelected ? 'border-blue-400 bg-gradient-to-br from-blue-50 to-white shadow-sm ring-1 ring-blue-100' : 'border-zinc-200 bg-white hover:border-zinc-300'
      }`}
      data-testid={`bank-account-card-${account.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-blue-600' : 'bg-blue-100'}`}>
            <Building2 className={`h-4 w-4 ${isSelected ? 'text-white' : 'text-blue-700'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm text-zinc-950 truncate">{account.bank_name}</div>
            <div className="text-[11px] text-zinc-500 truncate">{account.account_label}</div>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(account); }}
          className="opacity-0 group-hover:opacity-100 transition p-1 rounded hover:bg-zinc-100"
          data-testid={`bank-account-edit-${account.id}`}
        >
          <Edit3 className="h-3.5 w-3.5 text-zinc-500" />
        </button>
      </div>
      <div className="text-2xl font-bold tracking-tight text-zinc-950 tabular-nums" data-testid={`bank-account-balance-${account.id}`}>
        {fxof(account.computed_balance ?? account.balance ?? 0)}
      </div>
      <div className="text-[11px] text-zinc-500 mt-1 flex items-center justify-between">
        <span className="font-mono">N° {account.account_number}</span>
        {variation !== 0 && (
          <span className={`inline-flex items-center gap-0.5 font-bold ${variation >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {variation >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {variation >= 0 ? '+' : ''}{fmt(variation)} /7j
          </span>
        )}
      </div>
    </button>
  );
};

/* ────────────────── MAIN PAGE ────────────────── */
const BankReconciliationInner = () => {
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accountDialog, setAccountDialog] = useState({ open: false, account: null });
  const [txDialog, setTxDialog] = useState({ open: false, tx: null });
  const [uploadDialog, setUploadDialog] = useState(false);
  const [reconDialog, setReconDialog] = useState({ open: false, tx: null });
  const [reconKpis, setReconKpis] = useState(null);

  // Pagination + sélection multiple (Fév 2026)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Période bancaire (PM 24/02/2026) — déclaré avant fetchTx pour éviter TDZ
  const [currentPeriod, setCurrentPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [granularity, setGranularity] = useState('month'); // month | week | day
  const [subPeriod, setSubPeriod] = useState(''); // YYYY-Www ou YYYY-MM-DD
  const [closureStatusForPicker, setClosureStatusForPicker] = useState(null);
  const [selectedTxIds, setSelectedTxIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [catDialog, setCatDialog] = useState({ open: false, txIds: [] });
  // Onglet actif (Fév 2026)
  const [subTab, setSubTab] = useState('operations'); // operations | analytics
  // Filtre KPI cliqué (Fév 2026) — filtre visuel appliqué au tableau
  const [kpiFilter, setKpiFilter] = useState(null);

  const fetchAll = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);
    try {
      const acc = await axios.get(`${API}/bank/accounts`);
      setAccounts(acc.data.items || []);
      if (!selectedAccountId && acc.data.items?.length) {
        setSelectedAccountId(acc.data.items[0].id);
      }
    } catch {
      // silent
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [selectedAccountId]);

  const txFilterParams = useMemo(() => {
    const params = { account_id: selectedAccountId };
    if (granularity === 'month' && currentPeriod) {
      params.period = currentPeriod;
    } else if (granularity === 'week' && subPeriod) {
      const m = /^(\d{4})-W(\d{2})$/.exec(subPeriod);
      if (m) {
        const y = +m[1]; const wk = +m[2];
        const jan4 = new Date(Date.UTC(y, 0, 4));
        const jan4Day = jan4.getUTCDay() || 7;
        const mon = new Date(jan4);
        mon.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (wk - 1) * 7);
        const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 6);
        const f = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
        params.date_from = f(mon);
        params.date_to = f(sun);
      }
    } else if (granularity === 'day' && subPeriod) {
      params.date_from = subPeriod;
      params.date_to = subPeriod;
    }
    return params;
  }, [selectedAccountId, granularity, currentPeriod, subPeriod]);

  const fetchTx = useCallback(async () => {
    if (!selectedAccountId) { setTransactions([]); setTotal(0); setReconKpis(null); return; }
    try {
      const params = { ...txFilterParams, limit: pageSize, skip: (page - 1) * pageSize };
      const [tx, kpis] = await Promise.all([
        axios.get(`${API}/bank/transactions`, { params }),
        axios.get(`${API}/bank/accounts/${selectedAccountId}/reconciliation-kpis`),
      ]);
      setTransactions(tx.data.items || []);
      setTotal(tx.data.total || 0);
      setReconKpis(kpis.data);
    } catch {
      setTransactions([]); setTotal(0); setReconKpis(null);
    }
  }, [selectedAccountId, page, pageSize, txFilterParams]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchTx(); }, [fetchTx]);
  // Période intelligente (22/06/2026) : ouvrir sur le dernier mois alimenté du compte
  // si le mois courant ne contient aucune opération.
  const [periodAutoDone, setPeriodAutoDone] = useState(false);
  useEffect(() => {
    if (!selectedAccountId || periodAutoDone || granularity !== 'month') return;
    let cancelled = false;
    axios.get(`${API}/bank/periods/latest`, { params: { account_id: selectedAccountId } })
      .then(({ data }) => {
        if (cancelled) return;
        setPeriodAutoDone(true);
        if (!data?.latestPeriod || data.currentPeriodCount > 0) return;
        if (data.latestPeriod === data.currentPeriod) return;
        setCurrentPeriod(data.latestPeriod);
        toast({ title: `Période ouverte sur ${data.latestPeriod}`,
          description: 'Aucune opération sur le mois courant — dernier mois alimenté affiché.' });
      })
      .catch(() => setPeriodAutoDone(true));
    return () => { cancelled = true; };
  }, [selectedAccountId, periodAutoDone, granularity]);
  useEffect(() => { setPage(1); setSelectedTxIds(new Set()); }, [selectedAccountId, pageSize]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const allOnPageSelected = transactions.length > 0 && transactions.every(t => selectedTxIds.has(t.id));
  const toggleSelect = (id) => {
    setSelectedTxIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleSelectAllPage = () => {
    setSelectedTxIds(prev => {
      const n = new Set(prev);
      if (allOnPageSelected) transactions.forEach(t => n.delete(t.id));
      else transactions.forEach(t => n.add(t.id));
      return n;
    });
  };
  const clearSelection = () => setSelectedTxIds(new Set());

  // Tout sélectionner sur la période filtrée (au-delà de la page affichée)
  const selectAllFiltered = async () => {
    try {
      const { data } = await axios.get(`${API}/bank/transactions`, {
        params: { ...txFilterParams, limit: 500, skip: 0 },
      });
      const ids = (data.items || []).map(t => t.id);
      setSelectedTxIds(new Set(ids));
      if ((data.total || 0) > ids.length) {
        toast({ title: `${ids.length} opérations sélectionnées`,
          description: `Limite de 500 lignes par lot (${data.total} au total sur la période).` });
      }
    } catch (e) {
      toast({ title: 'Sélection impossible', description: e?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const applySuggestionOne = async (tx) => {
    try {
      const { data } = await axios.post(`${API}/bank/transactions/apply-suggestions`, { tx_ids: [tx.id] });
      if (data.applied) toast({ title: `Classée → ${data.details[0]?.label}` });
      else toast({ title: 'Aucune suggestion applicable', variant: 'destructive' });
      fetchTx();
    } catch (e) {
      toast({ title: 'Échec', description: e?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const bulkAction = async (action) => {
    if (selectedTxIds.size === 0) return;
    const ids = [...selectedTxIds];
    const labels = { reconcile: 'Rapprochement', ignore: 'Mise en ignoré', delete: 'Suppression', export: 'Export CSV', suggestions: 'Classement assisté' };
    if (action !== 'export' && !confirmBulk(ids.length, labels[action])) return;
    setBulkBusy(true);
    try {
      if (action === 'suggestions') {
        const { data } = await axios.post(`${API}/bank/transactions/apply-suggestions`, { tx_ids: ids });
        toast({
          title: `${data.applied} opération(s) classée(s) automatiquement`,
          description: (data.skipped || []).length ? `${data.skipped.length} sans suggestion ou déjà classée(s)` : undefined,
        });
      } else if (action === 'reconcile') {
        await axios.post(`${API}/bank/reconcile/bulk`, { account_id: selectedAccountId, tx_ids: ids });
        toast({ title: 'Rapprochement bulk lancé', description: `${ids.length} tx` });
      } else if (action === 'ignore') {
        const { data } = await axios.post(`${API}/bank/transactions/bulk-ignore`, { tx_ids: ids });
        toast({ title: 'Ignorées en masse', description: `${data?.updated ?? ids.length} tx` });
      } else if (action === 'delete') {
        if (!window.confirm(`Supprimer définitivement ${ids.length} opération(s) ?`)) { setBulkBusy(false); return; }
        const { data } = await axios.post(`${API}/bank/transactions/bulk-delete`, { tx_ids: ids });
        toast({ title: 'Suppression en masse', description: `${data?.deleted ?? ids.length} tx` });
      } else if (action === 'export') {
        const rows = transactions.filter(t => selectedTxIds.has(t.id));
        const headers = ['Date', 'Libellé', 'Type', 'Débit', 'Crédit', 'Solde', 'Statut'];
        const csv = [
          headers.join(';'),
          ...rows.map(tx => {
            const isCredit = tx.direction === 'credit';
            return [
              tx.operation_date ? new Date(tx.operation_date).toLocaleDateString('fr-FR') : '',
              (tx.label || '').replace(/;/g, ','),
              typeLabel(tx.type),
              !isCredit ? tx.amount : '',
              isCredit ? tx.amount : '',
              tx.balance_after ?? '',
              tx.reconciliation_status || '',
            ].join(';');
          }),
        ].join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bank_${currentAccount?.bank_name?.replace(/\s+/g, '_') || 'export'}_${rows.length}tx.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({ title: 'Export CSV', description: `${rows.length} ligne(s)` });
        setBulkBusy(false);
        return;
      }
      clearSelection();
      fetchAll(true); fetchTx();
    } catch (e) {
      toast({ title: 'Erreur action groupée', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setBulkBusy(false);
    }
  };

  const currentAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId),
    [accounts, selectedAccountId]
  );

  const grandTotal = useMemo(
    () => accounts.reduce((s, a) => s + (a.computed_balance || 0), 0),
    [accounts]
  );

  const deleteTx = async (tx) => {
    if (!window.confirm(`Supprimer l'opération "${tx.label}" ?`)) return;
    try {
      await axios.delete(`${API}/bank/transactions/${tx.id}`);
      toast({ title: 'Opération supprimée' });
      fetchAll(true); fetchTx();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const [autoMatching, setAutoMatching] = useState(false);
  const [showExceptionsPanel, setShowExceptionsPanel] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const runAutoMatch = async () => {
    if (!selectedAccountId) return;
    if (!window.confirm('Lancer l\'auto-matching IA sur toutes les transactions non-pointées ?')) return;
    setAutoMatching(true);
    try {
      const r = await axios.post(`${API}/bank/accounts/${selectedAccountId}/auto-match`, { use_llm: true });
      const s = r.data.stats || {};
      toast({
        title: `Auto-matching terminé ✅`,
        description: `${s.matched}/${s.total} pointées · ${s.suggested} suggestion(s) · ${s.unmatched} sans correspondance${r.data.llm_used ? ' · IA utilisée' : ''}`,
      });
      fetchAll(true); fetchTx();
    } catch (e) {
      toast({ title: 'Auto-matching échoué', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setAutoMatching(false); }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadReport = async () => {
    if (!selectedAccountId || !currentAccount) return;
    const period = new Date().toISOString().slice(0, 7);
    const input = window.prompt('Période du rapport (YYYY-MM) :', period);
    if (!input || !/^\d{4}-\d{2}$/.test(input)) return;
    setDownloadingPdf(true);
    try {
      const r = await axios.get(`${API}/bank/accounts/${selectedAccountId}/report`, {
        params: { period: input },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapprochement_${currentAccount.bank_name.replace(/\s+/g, '_')}_${input}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'PDF téléchargé' });
    } catch (e) {
      toast({ title: 'Erreur PDF', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setDownloadingPdf(false); }
  };

  if (loading) {
    return (
      <div className="space-y-4 p-1" data-testid="bank-page-loading">
        <div className="h-8 w-64 bg-zinc-100 rounded animate-pulse" />
        <div className="grid grid-cols-3 gap-3">
          {[1,2,3].map((i) => <div key={i} className="h-28 bg-zinc-100 rounded-xl animate-pulse" />)}
        </div>
        <div className="h-96 bg-zinc-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="bank-reconciliation-page">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
            <Landmark className="h-6 w-6 text-blue-600" />
            Rapprochement bancaire
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Multi-comptes · Saisie manuelle · {accounts.length > 0 ? `${accounts.length} compte${accounts.length > 1 ? 's' : ''} actif${accounts.length > 1 ? 's' : ''}` : 'Aucun compte configuré'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => fetchAll(true)} disabled={refreshing} data-testid="bank-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
          <Button variant="outline" size="sm" onClick={() => setUploadDialog(true)}
            disabled={accounts.length === 0} data-testid="bank-upload-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Importer relevé (OCR)
          </Button>
          <Button size="sm" onClick={() => setAccountDialog({ open: true, account: null })}
            className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bank-add-account-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nouveau compte
          </Button>
          {accounts.length > 0 && (
            <Button size="sm" onClick={() => setTxDialog({ open: true, tx: null })}
              className="bg-[#16A34A] hover:bg-[#15803d] text-white" data-testid="bank-add-tx-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nouvelle opération
            </Button>
          )}
        </div>
      </div>

      {/* TOTAL CARD */}
      {accounts.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-gradient-to-br from-blue-50 via-white to-emerald-50 p-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">
            Trésorerie bancaire consolidée
          </div>
          <div className="text-4xl font-bold tracking-tight text-zinc-950 tabular-nums" data-testid="bank-grand-total">
            {fxof(grandTotal)}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Somme des soldes de {accounts.length} compte{accounts.length > 1 ? 's' : ''} bancaire{accounts.length > 1 ? 's' : ''} actif{accounts.length > 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* ACCOUNTS GRID */}
      {accounts.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50/50 p-10 text-center" data-testid="bank-empty-state">
          <Landmark className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-zinc-700 mb-1">Aucun compte bancaire configuré</h3>
          <p className="text-sm text-zinc-500 mb-4 max-w-md mx-auto">
            Commencez par ajouter un compte bancaire (Versus Bank, SGCI, NSIA…) avec son solde initial.
            Vous pourrez ensuite saisir vos opérations quotidiennes (virements, chèques, frais…) et voir votre trésorerie consolidée.
          </p>
          <Button onClick={() => setAccountDialog({ open: true, account: null })}
            className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Ajouter mon premier compte
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bank-accounts-grid">
          {accounts.map((a) => (
            <AccountCard
              key={a.id} account={a} isSelected={a.id === selectedAccountId}
              onClick={() => setSelectedAccountId(a.id)}
              onEdit={(acc) => setAccountDialog({ open: true, account: acc })}
            />
          ))}
        </div>
      )}

      {/* PM 24/02/2026 — Sélecteur de période prominent (mois/semaine/jour) */}
      {currentAccount && (
        <PeriodPicker
          value={currentPeriod}
          onChange={(p) => { setCurrentPeriod(p); setSubPeriod(''); }}
          granularity={granularity}
          onGranularityChange={setGranularity}
          subPeriod={subPeriod}
          onSubPeriodChange={setSubPeriod}
          closureStatus={closureStatusForPicker}
          onOpenCloseDialog={() => setShowCloseDialog(true)}
        />
      )}

      {/* V2 (10/07/2026) — CFO Dashboard 8 KPIs cliquables (spec CFO) */}
      {currentAccount && (
        <BankCFODashboardCards
          accountId={selectedAccountId}
          period={currentPeriod}
          currentBalance={currentAccount.computed_balance ?? currentAccount.balance ?? 0}
          refreshKey={total}
          onFilterChange={(f) => { setSubTab('operations'); setKpiFilter(f); }}
          onClosureStatusChange={setClosureStatusForPicker}
        />
      )}

      {/* V2 — Bandeau actions Exceptions + Clôture */}
      {currentAccount && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="bank-v2-actions">
          <Button
            size="sm"
            variant={showExceptionsPanel ? 'default' : 'outline'}
            onClick={() => setShowExceptionsPanel(v => !v)}
            data-testid="bank-toggle-exceptions"
            className={showExceptionsPanel ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
          >
            {showExceptionsPanel ? 'Masquer' : 'Voir'} exceptions
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCloseDialog(true)}
            data-testid="bank-open-close-dialog"
          >
            🔒 Clôturer le mois {currentPeriod}
          </Button>
        </div>
      )}

      {/* V2 — Vue Exceptions déroulante */}
      {currentAccount && showExceptionsPanel && (
        <div className="bg-white border border-amber-200 rounded-xl p-4" data-testid="bank-exceptions-panel">
          <ExceptionsView
            accountId={selectedAccountId}
            yyyyMm={currentPeriod}
            refreshKey={total}
            onReconcile={(tx) => { setReconDialog({ open: true, tx }); }}
          />
        </div>
      )}

      {/* SOUS-ONGLETS Opérations / Analytics */}
      {currentAccount && (
        <div className="flex items-center gap-1 border-b border-zinc-200 bg-white rounded-t-xl px-3 pt-2">
          <button
            onClick={() => setSubTab('operations')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${subTab === 'operations' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            data-testid="bank-tab-operations"
          >
            <Wallet className="h-4 w-4" /> Opérations
          </button>
          <button
            onClick={() => setSubTab('analytics')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${subTab === 'analytics' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            data-testid="bank-tab-analytics"
          >
            <BarChart3 className="h-4 w-4" /> Analytics
            <span className="text-[10px] font-normal text-slate-400">BI dépenses</span>
          </button>
          <button
            onClick={() => setSubTab('rules')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${subTab === 'rules' ? 'border-amber-600 text-amber-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            data-testid="bank-tab-rules"
          >
            <Tag className="h-4 w-4" /> Règles de détection
            <span className="text-[10px] font-normal text-slate-400">charges récurrentes</span>
          </button>
        </div>
      )}

      {/* Panneau Règles de détection */}
      {currentAccount && subTab === 'rules' && (
        <div className="bg-white border border-zinc-200 border-t-0 rounded-b-xl p-4">
          <ExpenseRulesPanel />
        </div>
      )}

      {/* Panneau Analytics */}
      {currentAccount && subTab === 'analytics' && (
        <div className="bg-white border border-zinc-200 border-t-0 rounded-b-xl p-4">
          <BankAnalyticsTab
            accountId={selectedAccountId}
            period={currentPeriod}
            onChangePeriod={(p) => setCurrentPeriod(p)}
          />
        </div>
      )}

      {/* Bandeau filtre actif (KPI cliqué) */}
      {currentAccount && kpiFilter && subTab === 'operations' && (
        <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="bank-kpi-filter-banner">
          <div className="text-xs text-blue-800">
            <span className="font-semibold">Filtre actif :</span> {kpiFilter.__label}
            {Object.entries(kpiFilter).filter(([k]) => k !== '__label').map(([k, v]) => (
              <span key={k} className="ml-2 inline-block bg-white border border-blue-300 rounded px-1.5 py-0.5 text-[10px] font-mono">{k}={String(v)}</span>
            ))}
          </div>
          <button onClick={() => setKpiFilter(null)} className="text-xs text-blue-700 hover:underline" data-testid="bank-clear-kpi-filter">
            × Effacer le filtre
          </button>
        </div>
      )}

      {/* TRANSACTIONS TABLE */}
      {currentAccount && subTab === 'operations' && (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50/50 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-zinc-950 flex items-center gap-2">
                <Wallet className="h-4 w-4 text-blue-600" />
                Opérations · {currentAccount.bank_name}
              </h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                {total} opération{total > 1 ? 's' : ''} · Solde initial {fxof(currentAccount.initial_balance)} au{' '}
                {new Date(currentAccount.initial_balance_date).toLocaleDateString('fr-FR')}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {reconKpis && reconKpis.total > 0 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="recon-kpis">
                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> {reconKpis.reconciled_pct}% pointé
                  </Badge>
                  {reconKpis.unreconciled > 0 && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                      {reconKpis.unreconciled} à rapprocher
                    </Badge>
                  )}
                  {reconKpis.oldest_unreconciled_days > 30 && (
                    <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200">
                      Suspens {reconKpis.oldest_unreconciled_days}j
                    </Badge>
                  )}
                </div>
              )}
              {reconKpis && reconKpis.unreconciled > 0 && (
                <Button size="sm" variant="outline" onClick={runAutoMatch} disabled={autoMatching}
                  className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="bank-automatch-btn">
                  {autoMatching ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Analyse IA…</>
                  ) : (
                    <><Sparkles className="h-3.5 w-3.5 mr-1.5" />Rapprocher automatiquement</>
                  )}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={downloadReport} disabled={downloadingPdf}
                data-testid="bank-report-pdf-btn">
                {downloadingPdf ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />PDF…</>
                ) : (
                  <><FileDown className="h-3.5 w-3.5 mr-1.5" />Rapport PDF</>
                )}
              </Button>
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                Solde : {fxof(currentAccount.computed_balance)}
              </Badge>
            </div>
          </div>
          <div className="overflow-x-auto">
            {/* Bulk actions bar — barre partagée avec le module Wave (22/06/2026) */}
            {selectedTxIds.size > 0 && (
              <div className="px-4 pt-3" data-testid="bank-bulk-actions-bar">
                <BulkActionBar
                  count={selectedTxIds.size}
                  total={total}
                  label="opération(s)"
                  busy={bulkBusy}
                  onSelectAll={selectAllFiltered}
                  onClear={clearSelection}
                  testid="bank-bulk"
                  actions={[
                    { key: 'reconcile', label: 'Rapprocher', icon: <Sparkles className="h-3 w-3 mr-1" />,
                      className: 'border-violet-300 text-violet-700 hover:bg-violet-50',
                      onClick: () => bulkAction('reconcile') },
                    { key: 'categorize', label: 'Catégoriser', icon: <Tag className="h-3 w-3 mr-1" />,
                      className: 'border-teal-300 text-teal-700 hover:bg-teal-50',
                      onClick: () => setCatDialog({ open: true, txIds: [...selectedTxIds] }) },
                    { key: 'suggestions', label: 'Appliquer les suggestions', icon: <Lightbulb className="h-3 w-3 mr-1" />,
                      className: 'border-violet-300 text-violet-700 hover:bg-violet-50',
                      onClick: () => bulkAction('suggestions') },
                    { key: 'ignore', label: 'Ignorer', icon: <Ban className="h-3 w-3 mr-1" />,
                      className: 'border-amber-300 text-amber-700 hover:bg-amber-50',
                      onClick: () => bulkAction('ignore') },
                    { key: 'delete', label: 'Supprimer', icon: <Trash2 className="h-3 w-3 mr-1" />,
                      className: 'border-rose-300 text-rose-700 hover:bg-rose-50',
                      onClick: () => bulkAction('delete') },
                    { key: 'export', label: 'Export CSV', icon: <Download className="h-3 w-3 mr-1" />,
                      onClick: () => bulkAction('export') },
                  ]}
                />
              </div>
            )}

            <table className="w-full text-sm" data-testid="bank-tx-table">
              <thead>
                <tr className="border-b border-zinc-100 text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAllPage}
                      className="h-3.5 w-3.5 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      data-testid="bank-select-all"
                      title="Tout sélectionner sur cette page"
                    />
                  </th>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-3 py-2.5">Libellé</th>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-right px-3 py-2.5">Débit</th>
                  <th className="text-right px-3 py-2.5">Crédit</th>
                  <th className="text-right px-3 py-2.5">Solde</th>
                  <th className="text-left px-3 py-2.5">Catégorie</th>
                  <th className="text-center px-3 py-2.5">Statut</th>
                  <th className="text-center px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center py-10 text-zinc-400 text-sm">
                      Aucune opération. Cliquez sur <strong>« Nouvelle opération »</strong> ou <strong>« Importer relevé »</strong> pour commencer.
                    </td>
                  </tr>
                )}
                {(kpiFilter ? transactions.filter(tx => {
                  return Object.entries(kpiFilter).every(([k, v]) => {
                    if (k === '__label') return true;
                    if (k === 'direction') return tx.direction === v;
                    if (k === 'reconciliation_status') return tx.reconciliation_status === v;
                    if (k === 'type') return tx.type === v || (v === 'frais_bancaires' && ['frais_bancaires', 'agios', 'commission'].includes(tx.type));
                    if (k === 'category') return v === null ? !tx.category : tx.category === v;
                    return true;
                  });
                }) : transactions).map((tx) => {
                  const meta = typeMeta(tx.type);
                  const isCredit = tx.direction === 'credit';
                  const isReconciled = tx.reconciliation_status === 'reconciled';
                  const isIgnored = tx.reconciliation_status === 'ignored';
                  const isChecked = selectedTxIds.has(tx.id);
                  return (
                    <tr key={tx.id} className={`border-b border-zinc-50 hover:bg-zinc-50/40 ${isChecked ? 'bg-blue-50/30' : ''}`} data-testid={`bank-tx-row-${tx.id}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelect(tx.id)}
                          className="h-3.5 w-3.5 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          data-testid={`bank-tx-check-${tx.id}`}
                        />
                      </td>
                      <td className="px-4 py-2 tabular-nums text-xs text-zinc-600">
                        {new Date(tx.operation_date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-3 py-2 max-w-[260px]">
                        <div className="font-medium text-zinc-900 truncate" title={tx.label}>{tx.label}</div>
                        {tx.counterparty && (
                          <div className="text-[11px] text-zinc-500 truncate">{tx.counterparty}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] bg-${meta.color}-50 text-${meta.color}-700 border-${meta.color}-200`}>
                          {typeLabel(tx.type)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700 font-semibold">
                        {!isCredit ? fmt(tx.amount) : ''}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">
                        {isCredit ? fmt(tx.amount) : ''}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-600 text-xs">
                        {tx.balance_after != null ? fmt(tx.balance_after) : '—'}
                      </td>
                      <td className="px-3 py-2 max-w-[190px]">
                        {tx.category ? (
                          <button onClick={() => setCatDialog({ open: true, txIds: [tx.id] })}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-800 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded hover:bg-teal-100 truncate max-w-full"
                            title={tx.category_auto ? `Proposé automatiquement — ${tx.category_suggestion_source || ''}` : 'Modifier la catégorie'}
                            data-testid={`bank-tx-category-${tx.id}`}>
                            <Tag className="h-3 w-3 shrink-0" />
                            <span className="truncate">{tx.category_label || tx.category}</span>
                            {tx.category_auto && <span className="text-teal-600">·auto</span>}
                          </button>
                        ) : tx.suggested_category ? (
                          <div className="flex items-center gap-1">
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-violet-800 bg-violet-50 border border-dashed border-violet-300 px-2 py-0.5 rounded truncate max-w-[120px]"
                              title={`Proposition : ${tx.suggestion_source}`}
                              data-testid={`bank-tx-suggestion-${tx.id}`}>
                              <Lightbulb className="h-3 w-3 shrink-0" />
                              <span className="truncate">{tx.suggested_category_label}</span>
                            </span>
                            <button onClick={() => applySuggestionOne(tx)}
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-600"
                              title="Valider cette catégorie"
                              data-testid={`bank-tx-suggestion-accept-${tx.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setCatDialog({ open: true, txIds: [tx.id] })}
                            className="text-[11px] text-zinc-400 hover:text-teal-700 underline underline-offset-2"
                            data-testid={`bank-tx-categorize-${tx.id}`}>
                            Classer
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isReconciled ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded" data-testid={`status-${tx.id}`}>
                            <CheckCircle2 className="h-3 w-3" /> Pointé
                          </span>
                        ) : isIgnored ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded" data-testid={`status-${tx.id}`}>
                            <MinusCircle className="h-3 w-3" /> Ignorée
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-[10px] font-semibold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded" data-testid={`status-${tx.id}`}>
                            À pointer
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => setTxDialog({ open: true, tx })}
                            className="p-1 rounded hover:bg-amber-50 text-zinc-400 hover:text-amber-600"
                            data-testid={`bank-tx-edit-${tx.id}`}
                            title="Modifier"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setReconDialog({ open: true, tx })}
                            className="p-1 rounded hover:bg-blue-50 text-zinc-400 hover:text-blue-600"
                            data-testid={`bank-tx-reconcile-${tx.id}`}
                            title="Rapprocher"
                          >
                            <Link2 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => deleteTx(tx)}
                            className="p-1 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600"
                            data-testid={`bank-tx-delete-${tx.id}`}
                            title="Supprimer">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="px-4 py-2.5 border-t border-zinc-100 bg-zinc-50/50 flex items-center justify-between flex-wrap gap-2 text-xs" data-testid="bank-pagination-bar">
              <div className="flex items-center gap-2 text-zinc-600">
                <span>Lignes par page :</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="border border-zinc-300 rounded px-1.5 py-0.5 bg-white"
                  data-testid="bank-page-size-select"
                >
                  {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-zinc-500">
                  {total === 0 ? '0' : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)}`} sur {total}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="p-1 rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="bank-pagination-prev"
                  aria-label="Page précédente"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-zinc-700 font-medium">Page {page} / {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  className="p-1 rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="bank-pagination-next"
                  aria-label="Page suivante"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AccountDialog
        open={accountDialog.open}
        account={accountDialog.account}
        onClose={() => setAccountDialog({ open: false, account: null })}
        onSaved={() => fetchAll(true)}
      />
      <TxDialog
        open={txDialog.open}
        editingTx={txDialog.tx}
        accountId={selectedAccountId}
        accounts={accounts}
        onClose={() => setTxDialog({ open: false, tx: null })}
        onSaved={() => { fetchAll(true); fetchTx(); }}
      />
      <StatementUploadDialog
        open={uploadDialog}
        accountId={selectedAccountId}
        accountLabel={currentAccount ? `${currentAccount.bank_name} — ${currentAccount.account_label}` : ''}
        onClose={() => setUploadDialog(false)}
        onImported={() => { fetchAll(true); fetchTx(); }}
      />
      <ReconciliationDialog
        open={reconDialog.open}
        tx={reconDialog.tx}
        onClose={() => setReconDialog({ open: false, tx: null })}
        onDone={() => { fetchAll(true); fetchTx(); }}
      />
      <ClosePeriodDialog
        open={showCloseDialog}
        accountId={selectedAccountId}
        yyyyMm={currentPeriod}
        onClose={() => setShowCloseDialog(false)}
        onClosed={() => { fetchAll(true); fetchTx(); }}
      />
      <BankCategorizeDialog
        open={catDialog.open}
        txIds={catDialog.txIds}
        onClose={() => setCatDialog({ open: false, txIds: [] })}
        onSuccess={() => { clearSelection(); fetchTx(); }}
      />
    </div>
  );
};

export default function BankReconciliationPage() {
  return (
    <RequirePermission anyOf={[PERMS.FINANCE_COCKPIT_VIEW, PERMS.WAVE_VIEW]}>
      <BankReconciliationInner />
    </RequirePermission>
  );
}
